import { logger } from "../logger";
import { getConfiguredApiBaseUrl } from "./instances";

const CACHE_KEY = "client_credentials_token_response";
const EXPIRATION_SKEW_MS = 60 * 1000;

let memoryCache = null;

function decodeJwtClaims(token) {
  try {
    const payload = token.split(".")[1];
    const atobFn = globalThis.atob;
    if (!payload || typeof atobFn !== "function") return {};

    const base64 = payload.replace(/-/g, "+").replace(/_/g, "/");
    const padded = base64.padEnd(Math.ceil(base64.length / 4) * 4, "=");
    const decoded = atobFn(padded);
    const json = decodeURIComponent(
      decoded
        .split("")
        .map((char) => `%${`00${char.charCodeAt(0).toString(16)}`.slice(-2)}`)
        .join("")
    );

    return JSON.parse(json);
  } catch (error) {
    logger.warn("[Auth] Could not decode client credentials token claims:", error.message);
    return {};
  }
}

function getExpiresAt(claims, expiresIn) {
  if (typeof claims?.exp === "number") {
    return claims.exp * 1000;
  }

  if (typeof expiresIn === "number") {
    return Date.now() + expiresIn * 1000;
  }

  return Date.now() + 5 * 60 * 1000;
}

function isUsableCache(cache) {
  return cache?.accessToken && cache.expiresAt - Date.now() > EXPIRATION_SKEW_MS;
}

function readSessionCache() {
  try {
    const raw = sessionStorage.getItem(CACHE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function writeSessionCache(cache) {
  try {
    sessionStorage.setItem(CACHE_KEY, JSON.stringify(cache));
  } catch {
    // Ignore storage failures; memory cache still works for this tab.
  }
}

async function requestToken() {
  const tenant = process.env.REACT_APP_CLIENT_CREDENTIALS_TENANT || "";
  const baseUrl = getConfiguredApiBaseUrl(tenant) || "";
  const tokenUrl = baseUrl ? `${baseUrl.replace(/\/$/, "")}/public/client-token` : "";

  if (!tokenUrl) {
    throw new Error("Missing client credentials token configuration");
  }

  const response = await fetch(tokenUrl, {
    method: "POST",
  });

  const data = await response.json().catch(() => ({}));

  if (!response.ok || !data.access_token) {
    throw new Error(data.error_description || data.error || "Could not get client credentials token");
  }

  const claims = decodeJwtClaims(data.access_token);
  const cache = {
    accessToken: data.access_token,
    claims,
    expiresAt: getExpiresAt(claims, data.expires_in),
  };

  memoryCache = cache;
  writeSessionCache(cache);

  return cache;
}

export async function getClientCredentialsToken(options = {}) {
  const { forceRefresh = false } = options;

  if (!forceRefresh && isUsableCache(memoryCache)) {
    return {
      accessToken: memoryCache.accessToken,
      idToken: null,
      idTokenClaims: memoryCache.claims,
    };
  }

  if (!forceRefresh) {
    const sessionCache = readSessionCache();
    if (isUsableCache(sessionCache)) {
      memoryCache = sessionCache;
      return {
        accessToken: sessionCache.accessToken,
        idToken: null,
        idTokenClaims: sessionCache.claims,
      };
    }
  }

  const cache = await requestToken();
  return {
    accessToken: cache.accessToken,
    idToken: null,
    idTokenClaims: cache.claims,
  };
}
